import Logger from '../lib/logger';

// -----------------------------------------------------------------------------
// CartGifts Web Component
// -----------------------------------------------------------------------------
// Provides "Gift With Purchase" functionality entirely via JS—no Liquid markup needed.
// Global toggle allows enabling/disabling the entire module.

/**
 * Global on/off switch for the Gift With Purchase module.
 * Set to false to completely disable GWP behavior, hide UI, and remove gift SKUs.
 */
const GWP_ENABLED = false;

/**
 * Trigger modes for Gift With Purchase
 * @readonly
 * @enum {string}
 */
const GWP_MODES = {
	THRESHOLD: 'threshold',           // Spend threshold trigger
	PRODUCT_IN_CART: 'productInCart', // Product-in-cart trigger
};

/**
 * Configuration for each gift slot.
 */
const GIFT_CONFIGS = [
	{
		handle: 'shaker-bottle-free-gift',    // Shopify product handle for gift #1
		mode: GWP_MODES.PRODUCT_IN_CART,              // Unlock when specific product is in cart
		triggerVariantId: [52936867152235],             // Variant ID that triggers this gift
		triggerProductTitle: 'BetterCollagen+', // Display name for user-facing message
		enabled: true,                                // Toggle this gift on/off
	},
	// {
	// 	handle: 'onthego-box-gwp',                    // Shopify product handle for gift #2
	// 	mode: GWP_MODES.THRESHOLD,                    // Unlock when spend threshold reached
	// 	threshold: 25000,                             // Threshold in cents (e.g. $250.00)
	// 	enabled: false,                                // Toggle this gift on/off
	// },
];

/**
 * Represents a Gift With Purchase product and its eligibility logic.
 */
class Gift {
	/**
	 * @param {object} config
	 */
	constructor({ handle, mode, triggerVariantId = null, threshold = null, enabled = true, triggerProductTitle = '' }) {
		this.handle = handle;
		this.mode = mode;
		this.triggerVariantId = triggerVariantId;
		this.threshold = threshold;
		this.configEnabled = enabled;
		this.triggerProductTitle = triggerProductTitle;
		this.enabled = false;
		this.inCart = false;
	}

	/**
	 * Load product data and set config validity.
	 */
	async load() {
		try {
			const url = `${window.Shopify.routes.root}products/${this.handle}.js`;
			const resp = await fetch(url);
			if (!resp.ok) throw new Error(`Failed to load product: ${this.handle}`);
			const data = await resp.json();
			this.title = data.title;
			this.variants = data.variants;
			this.giftVariantId = data.variants[0].id;
			this.featuredImage = data.featured_image;
			if (this.mode === GWP_MODES.THRESHOLD && this.threshold == null) {
				this.threshold = data.threshold || 0;
			}
			const configValid = (
				(this.mode === GWP_MODES.THRESHOLD && this.threshold > 0) ||
				(this.mode === GWP_MODES.PRODUCT_IN_CART && this.triggerVariantId)
			);
			this.enabled = this.configEnabled && configValid;
		} catch (err) {
			Logger.error('CartGifts > Gift.load', err);
			this.enabled = false;
		}
	}

	/**
	 * Update inCart status based on cart data.
	 */
	updateCartStatus(cartData) {
		this.inCart = !!cartData.items.find(item => item.id === this.giftVariantId);
	}

	/**
	 * Check if the gift is eligible based on trigger mode.
	 */
	isEligible(cartData) {
		if (!this.enabled) return false;
		if (this.mode === GWP_MODES.THRESHOLD) {
			return cartData.original_total_price >= this.threshold;
		}
		if (this.mode === GWP_MODES.PRODUCT_IN_CART) {

			// If this.triggerVariantId is an array, check if any of the variants are in the cart
			if (Array.isArray(this.triggerVariantId)) {
				return this.triggerVariantId.some(id => cartData.items.find(item => item.id === id));
			}
			// Otherwise, check if the specific variant is in the cart
			return !!cartData.items.find(item => item.id === this.triggerVariantId);
		}
		return false;
	}
}

/**
 * Core logic for CartGifts: inject UI, monitor cart, and sync gifts.
 */
class CartGiftsLogic {
	constructor(rootEl) {
		this.container = rootEl;
		this.gifts = GIFT_CONFIGS.map(cfg => new Gift(cfg));
		this.cartData = null;
		this.productTitleEls = null;
		this.imageEl = null;
		this.headlineEl = null;
		this.instructionsEl = null;
	}

	/**
	 * Initialize component: handle global toggle, inject UI, load gifts, sync cart.
	 */
	async init() {
		// If globally disabled, hide and remove gift SKUs
		if (!GWP_ENABLED) {
			this.container.style.display = 'none';
			await Promise.all(this.gifts.map(g => g.load()));
			this.cartData = await this.fetchCart();
			if (this.cartData) {
				const updates = {};
				this.gifts.forEach(g => {
					g.updateCartStatus(this.cartData);
					if (g.inCart) updates[g.giftVariantId] = 0;
				});
				if (Object.keys(updates).length) {
					await fetch('/cart/update.js', {
						method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
					});
				}
			}
			return;
		}

		// Inject banner HTML if missing
		if (!this.container.querySelector('.cart-gifts__banner')) {
			this.container.innerHTML = `
        <div class="cart-gifts__banner">
          <h3></h3>
          <div class="cart-gifts__meter"><div class="cart-gifts__meter__line"></div></div>
          <div class="cart-gifts__locked-item">
            <div class="cart-gifts__locked-item__image"><img src="" width="63" height="96" loading="lazy" /></div>
            <div class="cart-gifts__locked-item__content">
              <div class="cart-gifts__locked-item__content__title">
                <h4 class="cart-gifts__product-title"></h4>
                <p class="cart-gifts__instructions"></p>
              </div>
              <div class="cart-gifts__locked-item__content__locked">Locked</div>
            </div>
          </div>
        </div>`;
		}

		// Cache UI elements
		this.productTitleEls = this.container.querySelectorAll('.cart-gifts__product-title');
		this.imageEl = this.container.querySelector('.cart-gifts__locked-item__image img');
		this.headlineEl = this.container.querySelector('h3');
		this.instructionsEl = this.container.querySelector('.cart-gifts__instructions');

		// Load gift data and perform initial sync
		await Promise.all(this.gifts.map(g => g.load()));
		await this.onCartChange();
	}

	/** Fetch current cart data. */
	async fetchCart() {
		const resp = await fetch('/cart.js');
		return resp.ok ? resp.json() : null;
	}

	/**
	 * Handle cart updates: refresh UI and sync gifts.
	 */
	async onCartChange() {
		if (!GWP_ENABLED) return;
		this.cartData = await this.fetchCart(); if (!this.cartData) return;
		this.gifts.forEach(g => g.updateCartStatus(this.cartData));
		this.renderBanner();
		await this.syncCart();
	}

	/**
	 * Render banner state: next gift or final unlocked message.
	 */
	renderBanner() {
		const enabledGifts = this.gifts.filter(g => g.enabled);
		const totalEnabled = enabledGifts.length;
		const unlockedCount = enabledGifts.filter(g => g.inCart).length;
		const nextGift = enabledGifts.find(g => !g.isEligible(this.cartData));

		if (totalEnabled === 0) { this.container.style.display = 'none'; return; }
		if (unlockedCount >= totalEnabled) {
			// Hide the locked-item section when no more gifts remain
			const lockedItemEl = this.container.querySelector('.cart-gifts__locked-item');
			if (lockedItemEl) lockedItemEl.style.display = 'none';
			// Fill the progress meter to 100%
			const meterLine = this.container.querySelector('.cart-gifts__meter__line');
			if (meterLine) meterLine.style.width = '100%';
			// Display singular or plural success message
			if (totalEnabled === 1) {
				this.headlineEl.textContent = `You've unlocked your free gift!`;
			} else {
				this.headlineEl.textContent = `You've unlocked all ${totalEnabled} free gifts!`;
			}
			this.instructionsEl.textContent = '';
			this.container.style.display = '';
			return;
		}
		if (nextGift) {
			const titleText = nextGift.title.replace(' (Free Gift)', '');
			const imgUrl = nextGift.featuredImage ? `${nextGift.featuredImage}&width=126` : '';
			this.imageEl.src = imgUrl;
			this.productTitleEls.forEach(el => el.textContent = titleText);
			const prefix = unlockedCount ? `You've unlocked ${unlockedCount} gift${unlockedCount > 1 ? 's' : ''}! Next up:` : '';
			if (nextGift.mode === GWP_MODES.THRESHOLD) {
				const spent = this.cartData.original_total_price;
				const threshold = nextGift.threshold;
				const remaining = ((threshold - spent) / 100).toFixed(2);
				const pct = Math.min((spent / threshold) * 100, 100) + '%';
				this.headlineEl.innerHTML = `${prefix} You're $<span class="cart-gifts__dollars-away">${remaining}</span> away from a free <span class="cart-gifts__product-title">${titleText}</span>!`;
				this.instructionsEl.innerHTML = `Spend $<span class="cart-gifts__threshold">${(threshold / 100).toFixed(0)}</span> more to unlock it.`;
				this.container.querySelector('.cart-gifts__meter__line').style.width = pct;
			} else {
				this.headlineEl.innerHTML = `${prefix} Get a free <span class="cart-gifts__product-title">${titleText}</span> with your order!`;
				this.instructionsEl.textContent = `Add ${nextGift.triggerProductTitle} to unlock this gift.`;
			}
			this.container.style.display = '';
		}
	}

	/**
	 * Sync gift SKUs in cart via /cart/update.js call.
	 */
	async syncCart() {
		const updates = {};
		this.gifts.forEach(g => {
			const shouldHave = g.isEligible(this.cartData);
			if (shouldHave && !g.inCart) updates[g.giftVariantId] = 1;
			if (!shouldHave && g.inCart) updates[g.giftVariantId] = 0;
		});
		if (Object.keys(updates).length) {
			const resp = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
			if (resp.ok) {
				await this.onCartChange();
				const section = window.Shopify.theme.sections.instances.find(s => s.refreshQuickCart);
				if (section) section.refreshQuickCart();
				if (location.pathname === '/cart') location.reload();
			}
		}
	}
}

/**
 * Custom element <cart-gifts>
 */
export default class CartGifts extends HTMLElement {
	constructor() {
		super();
		this.logic = new CartGiftsLogic(this);
		this._onCartUpdate = () => this.logic.onCartChange();
		this._onRemoveClick = e => { if (e.target.matches('.cart__remove, [data-cart-remove], .cart__quantity input')) this.logic.onCartChange(); };
	}

	connectedCallback() {
		// Always run initialization, including removal logic if disabled
		this.logic.init();

		// If globally disabled, hide UI and skip event listeners (gifts already removed)
		if (!GWP_ENABLED) {
			this.style.display = 'none';
			return;
		}

		// Module enabled: set up cart event listeners and polling
		document.addEventListener('cart:updated', this._onCartUpdate);
		document.addEventListener('cart:refresh', this._onCartUpdate);
		document.addEventListener('click', this._onRemoveClick);
		this._pollInterval = setInterval(() => this.logic.onCartChange(), 2000);
	}

	disconnectedCallback() {
		document.removeEventListener('cart:updated', this._onCartUpdate);
		document.removeEventListener('cart:refresh', this._onCartUpdate);
		document.removeEventListener('click', this._onRemoveClick);
		clearInterval(this._pollInterval);
	}

	static get observedAttributes() { return ['cart-subtotal']; }
	attributeChangedCallback(name) { if (name === 'cart-subtotal') this.logic.onCartChange(); }
}

customElements.define('cart-gifts', CartGifts);
